import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import EmbedAppearanceSection from '../components/EmbedAppearanceSection';
import FirstRewardsSection from '../components/FirstRewardsSection';
import PreferredPartnersSection from '../components/PreferredPartnersSection';
import ScheduleSection from '../components/ScheduleSection';
import SchoolPicker, { renderSchoolLogo } from '../components/SchoolPicker';
import apiService from '../services/api';
import { normalizeHexInput, toPickerValue } from '../utils/embedAppearanceColors';
import '../styles/StaffPages.css';

const DEFAULT_POINTS_BANNER_COLOR = '#001C44';

/**
 * What the embed will call the program when `rewards_title` is blank.
 *
 * Mirrors `_derive_mascot_name` / `_derive_school_short` in
 * apps/school/views.py — mascot, else the school name with University/State/College
 * stripped, then its first word. "Utah Tech University" -> "Utah Rewards". Display
 * only: the server is still the authority, this just makes the placeholder honest
 * instead of guessing the abbreviation, which is not what the fallback uses.
 */
const deriveDefaultRewardsTitle = (school) => {
  if (!school) return '';
  let mascot = String(school.mascot || '').trim();
  if (!mascot) {
    const type = String(school.type || '').trim();
    if (type && !['university', 'college', 'state'].includes(type.toLowerCase())) {
      mascot = type;
    } else {
      let part = String(school.name || '').trim();
      if (/\sof\s/i.test(part)) part = part.split(/\s+of\s+/i).pop();
      const stripped = part.replace(/\b(university|state|college)\b\.?/gi, '').replace(/\s{2,}/g, ' ').trim();
      mascot = stripped || part;
    }
  }
  const short = mascot.split(/\s+/)[0] || mascot;
  return short ? `${short} Rewards` : '';
};

const buildSchoolFormData = (school) => ({
  logoFile: null,
  logoUrl: school?.image || '',
  bannerFile: null,
  bannerUrl: school?.banner_image || '',
  primary_color: normalizeHexInput(school?.primary_color) || DEFAULT_POINTS_BANNER_COLOR,
  rewards_title: school?.rewards_title || '',
  commission_split: school?.commission_split ?? 70,
  content_active: school?.content_active || false,
  travel_active: school?.travel_active !== false,
  is_stripe_feature_enabled: school?.is_stripe_feature_enabled || false,
  extension_mode: school?.extension_mode || 'built_in',
  extension_app_url: school?.extension_app_url || '',
  is_active: school?.is_active !== false,
});

const LB_TIMEZONE_OPTIONS = [
  { value: 'America/Denver', label: 'Mountain — Denver' },
  { value: 'America/Phoenix', label: 'Mountain — Phoenix (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
  { value: 'America/Anchorage', label: 'Alaska — Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii — Honolulu' },
  { value: 'America/Chicago', label: 'Central — Chicago' },
  { value: 'America/New_York', label: 'Eastern — New York' },
  { value: 'America/Detroit', label: 'Eastern — Detroit' },
  { value: 'UTC', label: 'UTC' },
];

const LB_WEEKDAY_OPTIONS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const LB_HOUR12_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}));

const LB_MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const m = i * 5;
  return { value: m, label: String(m).padStart(2, '0') };
});

const LB_AMPM_OPTIONS = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
];

const LB_PERIOD_OPTIONS = [1, 3, 5, 7, 14, 21, 28, 30].map((n) => ({
  value: n,
  label: `${n} days`,
}));

const LB_TOP_N_OPTIONS = [5, 10, 15, 20, 25, 50].map((n) => ({
  value: n,
  label: String(n),
}));

const LB_SOURCE_PRESETS = [
  { value: 'all', label: 'All types (full ledger)', csv: '' },
  { value: 'transaction', label: 'Purchases only', csv: 'transaction' },
  { value: 'tx_referral', label: 'Purchases + referrals', csv: 'transaction,referral' },
  { value: 'tx_manual', label: 'Purchases + manual', csv: 'transaction,manual' },
  {
    value: 'no_manual',
    label: 'All except manual',
    csv: 'transaction,commission,bonus,referral,signup,daily_login',
  },
  { value: 'custom', label: 'Custom (multi-select below)', csv: '__custom__' },
];

const LB_EARNING_MULTI = [
  { value: 'transaction', label: 'Purchases (transaction)' },
  { value: 'commission', label: 'Commission' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'referral', label: 'Referral' },
  { value: 'signup', label: 'Signup' },
  { value: 'daily_login', label: 'Daily login' },
  { value: 'manual', label: 'Manual' },
];

function h24To12(h24) {
  const h = Number(h24);
  if (h === 0) return { hour12: 12, ampm: 'AM' };
  if (h < 12) return { hour12: h, ampm: 'AM' };
  if (h === 12) return { hour12: 12, ampm: 'PM' };
  return { hour12: h - 12, ampm: 'PM' };
}

function h12To24(hour12, ampm) {
  let h = Number(hour12);
  if (ampm === 'AM') {
    if (h === 12) return 0;
    return h;
  }
  if (h === 12) return 12;
  return h + 12;
}

function detectEarningPreset(csv) {
  const t = (csv || '').trim();
  if (!t) return { preset: 'all', custom: [] };
  const norm = t
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
    .sort()
    .join(',');
  const row = LB_SOURCE_PRESETS.find((p) => p.csv === norm);
  if (row && row.value !== 'custom') return { preset: row.value, custom: [] };
  return {
    preset: 'custom',
    custom: t.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean),
  };
}

function mergeLeaderboardFormFromApi(data) {
  const { hour12, ampm } = h24To12(data.week_boundary_hour);
  const { preset, custom } = detectEarningPreset(data.included_earning_sources);
  return {
    ...data,
    leaderboard_active: data.leaderboard_active !== false,
    boundary_hour_12: hour12,
    boundary_ampm: ampm,
    earning_preset: preset,
    earning_custom: Array.isArray(custom) ? custom : [],
  };
}

function earningCsvFromForm(form) {
  if (form.earning_preset === 'custom') {
    return (form.earning_custom || []).join(',');
  }
  const p = LB_SOURCE_PRESETS.find((x) => x.value === form.earning_preset);
  if (!p || p.value === 'all') return '';
  return p.csv;
}

function timezoneSelectOptions(currentTz) {
  const cur = (currentTz || '').trim();
  const base = LB_TIMEZONE_OPTIONS.slice();
  if (cur && !base.some((o) => o.value === cur)) {
    base.unshift({ value: cur, label: `${cur} (current)` });
  }
  return base;
}

function scoringPeriodSelectOptions(currentDays) {
  const v = Number(currentDays) || 7;
  const set = new Set(LB_PERIOD_OPTIONS.map((o) => o.value));
  set.add(v);
  return Array.from(set)
    .sort((a, b) => a - b)
    .map((d) => ({ value: d, label: `${d} days` }));
}

function topRankSelectOptions(currentN) {
  const v = Number(currentN) || 10;
  const set = new Set(LB_TOP_N_OPTIONS.map((o) => o.value));
  set.add(Math.min(100, Math.max(1, v)));
  return Array.from(set)
    .sort((a, b) => a - b)
    .map((d) => ({ value: d, label: String(d) }));
}

const lbSelectStyle = {
  width: '100%',
  maxWidth: '360px',
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  backgroundColor: '#fff',
};

const PillGroup = ({ options, value, onChange, multi = false, disabled = false, size = 'md' }) => {
  const pad = size === 'sm' ? '10px 20px' : size === 'lg' ? '16px 32px' : '13px 26px';
  const fontSize = size === 'sm' ? '14px' : size === 'lg' ? '16px' : '15px';
  const minWidth = size === 'sm' ? 'auto' : size === 'lg' ? '140px' : '120px';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
      {options.map((o) => {
        const active = multi
          ? Array.isArray(value) && value.includes(o.value)
          : value === o.value;
        return (
          <button
            key={String(o.value)}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              if (multi) {
                const current = Array.isArray(value) ? value : [];
                if (current.includes(o.value)) {
                  onChange(current.filter((v) => v !== o.value));
                } else {
                  onChange([...current, o.value]);
                }
              } else {
                onChange(o.value);
              }
            }}
            style={{
              padding: pad,
              minWidth,
              backgroundColor: active ? '#00C8FF' : '#ffffff',
              border: active ? '1px solid #00C8FF' : '1px solid #dee2e6',
              borderRadius: '100px',
              fontFamily: '"Baikal Book"',
              fontSize,
              fontWeight: 340,
              color: active ? '#000000' : '#666666',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

const SuperuserSchoolManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('school-info');
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [formData, setFormData] = useState({
    logoFile: null,
    logoUrl: '',
    bannerFile: null,
    bannerUrl: '',
    primary_color: DEFAULT_POINTS_BANNER_COLOR,
    rewards_title: '',
    commission_split: '',
    content_active: false,
    travel_active: true,
    is_stripe_feature_enabled: false,
    extension_mode: 'built_in',
    extension_app_url: '',
    is_active: true
  });

  // Featured Offers state
  const [featuredOffers, setFeaturedOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newOffer, setNewOffer] = useState({
    name: '',
    offer_id: '',
    top_order: 0,
    bottom_order: 0,
    top_featured: false,
    bottom_featured: false,
    small_logo_url: '',
    large_logo_url: '',
    small_logo_file: null,
    large_logo_file: null,
    summary: '',
    is_active: true,
    is_global: false
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);

  const [leaderboardForm, setLeaderboardForm] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardSaving, setLeaderboardSaving] = useState(false);
  const [winnerSnapshot, setWinnerSnapshot] = useState(null);
  const [winnerSnapshotError, setWinnerSnapshotError] = useState(null);

  const [offerOrderView, setOfferOrderView] = useState('edit');
  const [offerOrderMerchants, setOfferOrderMerchants] = useState([]);
  const [offerOrderLoading, setOfferOrderLoading] = useState(false);
  const [offerOrderSaving, setOfferOrderSaving] = useState(false);
  const [offerOrderSearch, setOfferOrderSearch] = useState('');
  const [offerOrderDirty, setOfferOrderDirty] = useState({});
  const [offerOrderError, setOfferOrderError] = useState(null);
  const [offerOrderSuccess, setOfferOrderSuccess] = useState(null);

  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const smallLogoInputRef = useRef(null);
  const largeLogoInputRef = useRef(null);

  useEffect(() => {
    fetchSchools();
  }, []);

  useEffect(() => {
    if (searchParams.get('tab') === 'badges') {
      navigate('/superuser/challenge-presets/?tab=badges', { replace: true });
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    if (selectedSchool) {
      fetchFeaturedOffers();
    }
  }, [selectedSchool]);

  useEffect(() => {
    if (!selectedSchool || activeTab !== 'leaderboard') return undefined;
    let cancelled = false;
    (async () => {
      setLeaderboardLoading(true);
      setWinnerSnapshot(null);
      setWinnerSnapshotError(null);
      setError(null);
      try {
        const results = await Promise.allSettled([
          apiService.getSchoolLeaderboardConfig(selectedSchool.id),
          apiService.getSchoolLeaderboardLastWinner(selectedSchool.id),
        ]);
        if (cancelled) return;
        const [cfgRes, snapRes] = results;
        if (cfgRes.status === 'fulfilled') {
          setLeaderboardForm(mergeLeaderboardFormFromApi(cfgRes.value));
        } else {
          setLeaderboardForm(null);
          const err = cfgRes.reason;
          setError(
            err?.response?.data?.detail ||
              err?.response?.data?.error ||
              err?.message ||
              'Failed to load leaderboard settings',
          );
        }
        if (snapRes.status === 'fulfilled') {
          setWinnerSnapshot(snapRes.value);
        } else {
          const err = snapRes.reason;
          setWinnerSnapshotError(
            err?.response?.data?.detail ||
              err?.response?.data?.error ||
              err?.message ||
              'Failed to load last winner snapshot',
          );
        }
      } finally {
        if (!cancelled) setLeaderboardLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSchool, activeTab]);

  useEffect(() => {
    if (activeTab !== 'offer-order-in-app' || !selectedSchool) return undefined;
    let cancelled = false;
    (async () => {
      setOfferOrderLoading(true);
      setOfferOrderError(null);
      setOfferOrderSuccess(null);
      setOfferOrderDirty({});
      try {
        const data = await apiService.getWildfireOfferOrder({ schoolId: selectedSchool.id });
        if (!cancelled) setOfferOrderMerchants(data.results || []);
      } catch (err) {
        if (!cancelled) {
          setOfferOrderError(err?.response?.data?.error || err?.message || 'Failed to load offer order');
          setOfferOrderMerchants([]);
        }
      } finally {
        if (!cancelled) setOfferOrderLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, selectedSchool]);

  // Memoized merge of server rows + pending dirty edits, then filter + sort.
  // Recomputes only when merchants, dirty edits, or the search string change.
  const offerOrderDerived = useMemo(() => {
    const merged = offerOrderMerchants.map((m) => {
      const dirty = offerOrderDirty[m.wildfire_merchant_id];
      return {
        ...m,
        effective_order: dirty ? dirty.order_number : m.order_number,
        effective_is_global: dirty ? dirty.is_global : m.is_global,
      };
    });
    const q = offerOrderSearch.trim().toLowerCase();
    const filtered = q ? merged.filter((m) => (m.name || '').toLowerCase().includes(q)) : merged;
    const sortByOrder = (a, b) => {
      const ao = a.effective_order;
      const bo = b.effective_order;
      if (ao == null && bo == null) return (a.name || '').localeCompare(b.name || '');
      if (ao == null) return 1;
      if (bo == null) return -1;
      if (bo !== ao) return bo - ao;
      return (a.name || '').localeCompare(b.name || '');
    };
    const sorted = [...filtered].sort(sortByOrder);
    const pinnedPreview = merged.filter((m) => m.effective_order != null).sort(sortByOrder);
    return { merged, sorted, pinnedPreview };
  }, [offerOrderMerchants, offerOrderDirty, offerOrderSearch]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-menu')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-dismiss success and error messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    // Keep errors visible while the offer modal is open so the user can read them.
    if (error && !showAddForm) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, showAddForm]);

  const fetchSchools = async (preserveSelection = false) => {
    try {
      setLoading(true);
      const response = await apiService.getSchools();
      // Handle both array and paginated responses
      let schoolList = Array.isArray(response) ? response : (response.results || []);

      schoolList = [...schoolList].sort((a, b) => {
        const aActive = a?.is_active === false ? 1 : 0;
        const bActive = b?.is_active === false ? 1 : 0;
        if (aActive !== bActive) return aActive - bActive;
        return (a?.name || '').localeCompare(b?.name || '');
      });

      setSchools(schoolList);

      if (preserveSelection && selectedSchool) {
        const currentSchool = schoolList.find(s => s.id === selectedSchool.id);
        if (currentSchool) {
          setSelectedSchool(currentSchool);
          setFormData(buildSchoolFormData(currentSchool));
        } else {
          setSelectedSchool(null);
        }
      }
    } catch (err) {
      setError('Failed to load schools');
      console.error('Error fetching schools:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedOffers = async () => {
    try {
      setLoadingOffers(true);
      // Fetch featured offers for the selected school
      const response = await apiService.getFeaturedOffers(selectedSchool?.id);
      const offers = Array.isArray(response) ? response : (response.results || []);
      console.log('Featured offers received:', offers);
      setFeaturedOffers(offers);
    } catch (err) {
      console.error('Error fetching featured offers:', err);
      setFeaturedOffers([]);
    } finally {
      setLoadingOffers(false);
    }
  };

  const handleSchoolSelect = school => {
    if (!school) return;
    setSelectedSchool(school);
    setFormData(buildSchoolFormData(school));
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLeaderboardFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'leaderboard_active' || name === 'enabled' || name === 'include_null_earning_source') {
      setLeaderboardForm((prev) => ({ ...prev, [name]: value === 'true' }));
      return;
    }
    if (name === 'earning_preset') {
      setLeaderboardForm((prev) => ({
        ...prev,
        earning_preset: value,
        earning_custom: value === 'custom' ? prev.earning_custom || [] : [],
      }));
      return;
    }
    const numNames = new Set([
      'week_boundary_weekday',
      'boundary_hour_12',
      'week_boundary_minute',
      'scoring_period_days',
      'top_rank_count',
    ]);
    const nextVal = numNames.has(name) ? Number(value) : value;
    setLeaderboardForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : nextVal,
    }));
  };

  const handleEarningMultiChange = (e) => {
    const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
    setLeaderboardForm((prev) => ({
      ...prev,
      earning_preset: 'custom',
      earning_custom: selected,
    }));
  };

  const handleLeaderboardSave = async (e) => {
    e.preventDefault();
    if (!selectedSchool || !leaderboardForm) return;
    setLeaderboardSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        leaderboard_active: Boolean(leaderboardForm.leaderboard_active),
        enabled: Boolean(leaderboardForm.enabled),
        timezone: String(leaderboardForm.timezone || '').trim() || 'America/Denver',
        week_boundary_weekday: Number(leaderboardForm.week_boundary_weekday),
        week_boundary_hour: h12To24(
          Number(leaderboardForm.boundary_hour_12),
          leaderboardForm.boundary_ampm,
        ),
        week_boundary_minute: Number(leaderboardForm.week_boundary_minute),
        scoring_period_days: Number(leaderboardForm.scoring_period_days),
        top_rank_count: Number(leaderboardForm.top_rank_count),
        included_earning_sources: earningCsvFromForm(leaderboardForm),
        include_null_earning_source: Boolean(leaderboardForm.include_null_earning_source),
      };
      const out = await apiService.patchSchoolLeaderboardConfig(selectedSchool.id, payload);
      setLeaderboardForm(mergeLeaderboardFormFromApi(out));
      setSuccess('Leaderboard settings saved.');
    } catch (err) {
      const d = err.response?.data;
      let msg = err.message || 'Failed to save leaderboard settings';
      if (d && typeof d === 'object' && !Array.isArray(d)) {
        if (d.detail) msg = String(d.detail);
        else if (d.error) msg = String(d.error);
        else {
          const parts = Object.entries(d).map(([k, v]) => {
            if (Array.isArray(v)) return `${k}: ${v.join(', ')}`;
            if (typeof v === 'object') return `${k}: ${JSON.stringify(v)}`;
            return `${k}: ${v}`;
          });
          if (parts.length) msg = parts.join(' ');
        }
      }
      setError(msg);
    } finally {
      setLeaderboardSaving(false);
    }
  };

  // Logo upload handlers
  const handleLogoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files && files[0] && files[0].type.startsWith('image/')) {
      setFormData(prev => ({ ...prev, logoFile: files[0] }));
    }
  };

  const handleLogoFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setFormData(prev => ({ ...prev, logoFile: file }));
    }
  };

  const handleLogoUploadClick = () => {
    logoInputRef.current?.click();
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logoFile: null }));
  };

  // Banner upload handlers
  const handleBannerDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleBannerDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files && files[0] && files[0].type.startsWith('image/')) {
      setFormData(prev => ({ ...prev, bannerFile: files[0] }));
    }
  };

  const handleBannerFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setFormData(prev => ({ ...prev, bannerFile: file }));
    }
  };

  const handleBannerUploadClick = () => {
    bannerInputRef.current?.click();
  };

  const removeBanner = () => {
    setFormData(prev => ({ ...prev, bannerFile: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSchool) return;

    // Validate commission split
    const commission = parseFloat(formData.commission_split);
    if (isNaN(commission) || commission < 0 || commission > 100) {
      setError('Commission split must be between 0 and 100');
      return;
    }

    const primaryColor = normalizeHexInput(formData.primary_color);
    if (!primaryColor) {
      setError('Enter a valid hex color for the points banner (e.g. #001C44)');
      return;
    }

    const extensionMode = formData.extension_mode || 'built_in';
    const extensionAppUrl = String(formData.extension_app_url || '').trim();
    if (extensionMode === 'separate_app') {
      if (!extensionAppUrl) {
        setError('Enter the Apple landing page URL for the standalone extension app');
        return;
      }
      if (!/^https?:\/\//i.test(extensionAppUrl)) {
        setError('Extension app page must be an http(s) URL');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // First, upload images if we have files. Strip any server-appended
      // cache-bust param (?v=… or &v=…) so we don't persist it back to the
      // DB; the serializer re-applies it on every read.
      const stripBust = (u) =>
        typeof u === 'string'
          ? u.replace(/([?&])v=\d+(&|$)/, (_, lead, tail) => (lead === '?' && tail === '&' ? '?' : tail === '&' ? lead : '')).replace(/[?&]$/, '')
          : u;
      let logoUrl = stripBust(formData.logoUrl);
      let bannerUrl = stripBust(formData.bannerUrl);

      if (formData.logoFile || formData.bannerFile) {
        try {
          const uploadResponse = await apiService.uploadSchoolImages(
            selectedSchool.id,
            formData.logoFile,
            formData.bannerFile
          );

          // Get the URLs from the upload response
          if (uploadResponse.logo_url) {
            logoUrl = uploadResponse.logo_url;
          }
          if (uploadResponse.banner_url) {
            bannerUrl = uploadResponse.banner_url;
          }
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
          // If upload fails, fall back to URLs
        }
      }

      // Now update the school with all the data including the image URLs
      const updateData = {
        // Blank is a legitimate value: the embed falls back to "<abbreviation>
        // Rewards" when this is empty, so clearing it restores that default.
        rewards_title: String(formData.rewards_title || '').trim(),
        commission_split: commission,
        primary_color: primaryColor,
        content_active: formData.content_active,
        travel_active: formData.travel_active,
        is_stripe_feature_enabled: formData.is_stripe_feature_enabled,
        extension_mode: extensionMode,
        extension_app_url: extensionMode === 'separate_app' ? extensionAppUrl : '',
        is_active: formData.is_active === true
      };

      // Add image URLs if available
      if (logoUrl) {
        updateData.image = logoUrl;
      }
      if (bannerUrl) {
        updateData.banner_image = bannerUrl;
      }

      await apiService.updateSchool(selectedSchool.id, updateData);

      setSelectedSchool((prev) => (prev ? { ...prev, ...updateData } : prev));
      setSuccess('School settings updated successfully!');

      // Refresh schools list to get updated data, preserving current selection
      await fetchSchools(true);
    } catch (err) {
      setError('Failed to update school settings');
      console.error('Error updating school:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddOffer = async () => {
    if (!selectedSchool || !newOffer.name || !newOffer.offer_id) {
      setError('Name and Offer ID are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      let small_logo_url = newOffer.small_logo_url;
      let large_logo_url = newOffer.large_logo_url;

      // Upload images if we have files
      if (newOffer.small_logo_file || newOffer.large_logo_file) {
        try {
          const uploadResponse = await apiService.uploadOfferImages(
            newOffer.small_logo_file,
            newOffer.large_logo_file
          );

          if (uploadResponse.small_logo_url) {
            small_logo_url = uploadResponse.small_logo_url;
          }
          if (uploadResponse.large_logo_url) {
            large_logo_url = uploadResponse.large_logo_url;
          }
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
          setError('Failed to upload images. Please try again.');
          setSaving(false);
          return;
        }
      }

      const offerTypeForActiveTab =
        activeTab === 'featured-offers-in-app' ? 'click' : 'card_linked';
      const offerData = {
        name: newOffer.name,
        offer_id: newOffer.offer_id,
        top_order: newOffer.top_order,
        bottom_order: newOffer.bottom_order,
        top_featured: newOffer.top_featured,
        bottom_featured: newOffer.bottom_featured,
        small_logo_url: small_logo_url,
        large_logo_url: large_logo_url,
        summary: newOffer.summary,
        is_active: newOffer.is_active,
        school: newOffer.is_global ? null : selectedSchool.id,
        offer_type: editingOffer?.offer_type || offerTypeForActiveTab,
        is_preferred_partner: false,
      };

      if (editingOffer) {
        await apiService.updateFeaturedOffer(editingOffer.id, offerData);
        setSuccess('Featured offer updated successfully!');
      } else {
        await apiService.createFeaturedOffer(offerData);
        setSuccess('Featured offer added successfully!');
      }

      // Reset form
      setNewOffer({
        name: '',
        offer_id: '',
        top_order: 0,
        bottom_order: 0,
        top_featured: false,
        bottom_featured: false,
        small_logo_url: '',
        large_logo_url: '',
        small_logo_file: null,
        large_logo_file: null,
        summary: '',
        is_active: true
      });
      setEditingOffer(null);
      // Hide the form after successful addition
      setShowAddForm(false);

      // Refresh offers list
      await fetchFeaturedOffers();
    } catch (err) {
      const data = err?.response?.data;
      let message = null;
      if (data) {
        if (typeof data === 'string') message = data;
        else if (data.detail) message = data.detail;
        else if (data.error) message = data.error;
        else if (typeof data === 'object') {
          // DRF field-level errors: { field: ["msg", ...], ... }
          const parts = Object.entries(data).map(([field, val]) =>
            `${field}: ${Array.isArray(val) ? val.join(', ') : val}`
          );
          if (parts.length) message = parts.join(' • ');
        }
      }
      setError(message || err?.message || 'Failed to save featured offer');
      console.error('Error adding offer:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this featured offer?')) {
      return;
    }

    try {
      await apiService.deleteFeaturedOffer(offerId);
      setSuccess('Featured offer deleted successfully!');
      await fetchFeaturedOffers();
    } catch (err) {
      setError('Failed to delete featured offer');
      console.error('Error deleting offer:', err);
    }
  };

  const handleDeactivateOffer = async (offer, section) => {
    try {
      let updateData = {};

      // Check which sections the offer is featured in
      const isTopFeatured = offer.top_featured;
      const isBottomFeatured = offer.bottom_featured;

      if (isTopFeatured && isBottomFeatured) {
        // Offer is in both sections - just remove from the clicked section
        if (section === 'top') {
          updateData = { top_featured: false };
        } else if (section === 'bottom') {
          updateData = { bottom_featured: false };
        }
      } else {
        // Offer is only in one section - deactivate entirely
        updateData = { is_active: false };
      }

      await apiService.updateFeaturedOffer(offer.id, updateData);
      setSuccess('Featured offer updated successfully!');
      await fetchFeaturedOffers();
      setActiveDropdown(null);
    } catch (err) {
      setError('Failed to deactivate featured offer');
      console.error('Error deactivating offer:', err);
    }
  };

  const handleActivateOffer = async (offerId) => {
    try {
      await apiService.updateFeaturedOffer(offerId, { is_active: true });
      setSuccess('Featured offer activated successfully!');
      await fetchFeaturedOffers();
      setActiveDropdown(null);
    } catch (err) {
      setError('Failed to activate featured offer');
      console.error('Error activating offer:', err);
    }
  };

  const handleEditOffer = (offer) => {
    setEditingOffer(offer);
    setNewOffer({
      ...offer,
      small_logo_file: null,
      large_logo_file: null,
      is_global: offer.school == null
    });
    setShowAddForm(true);
    setActiveDropdown(null);
  };

  const handleCloseOfferModal = () => {
    if (saving) return;
    setShowAddForm(false);
    setEditingOffer(null);
    setError(null);
    setNewOffer({
      name: '',
      offer_id: '',
      top_order: 0,
      bottom_order: 0,
      top_featured: false,
      bottom_featured: false,
      small_logo_url: '',
      large_logo_url: '',
      small_logo_file: null,
      large_logo_file: null,
      summary: '',
      is_active: true,
      is_global: false
    });
  };

  const handleUpdateOfferOrder = async (offerId, orderType, newOrder) => {
    try {
      const updateData = {};
      updateData[orderType] = newOrder;
      await apiService.updateFeaturedOffer(offerId, updateData);
      await fetchFeaturedOffers();
    } catch (err) {
      console.error('Error updating offer order:', err);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
            <div style={{ padding: '40px', textAlign: 'center' }}>
              Loading schools...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
          <h1 className="staff-page-header">Manage Schools</h1>
          <p className="staff-page-subheader" style={{ marginBottom: '24px' }}>Manage school settings, featured offers, and weekly leaderboard rules (superuser only).</p>

          {/* School Selector — sticks to the top so the school in context stays
              visible (and switchable) however far down the page you scroll. */}
          <div style={{
            position: 'sticky',
            top: 0,
            zIndex: 30,
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            // No top margin: a sticky element's offset is measured to its margin
            // box, so 24px here would pin the bar 24px below the scrollport. The
            // spacing lives on the subheader above instead.
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)'
          }}>
            {schools.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                No schools found. Please create schools first.
              </div>
            ) : (
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151'
                }}>
                  Select School
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '40px', flexWrap: 'wrap' }}>
                <div style={{ width: '400px', maxWidth: '100%' }}>
                  <SchoolPicker schools={schools} value={selectedSchool} onChange={handleSchoolSelect} />
                </div>

                  {/* The school in context, beside the picker rather than only
                      inside it — the bar is sticky, so this is what tells you
                      which school the settings below belong to. */}
                  {selectedSchool ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
                      {renderSchoolLogo(selectedSchool, 64)}
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontFamily: '"Baikal Condensed", "Baikal Book"',
                            fontSize: '28px',
                            fontWeight: 900,
                            lineHeight: 1.15,
                            color: '#111827',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {selectedSchool.name}
                        </div>
                        <div
                          style={{
                            fontFamily: '"Baikal Book"',
                            fontSize: '14px',
                            color: '#6b7280',
                          }}
                        >
                          {[
                            [selectedSchool.city, selectedSchool.state].filter(Boolean).join(', '),
                            selectedSchool.is_active === false ? 'Inactive' : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          {!selectedSchool ? (
            <p className="staff-page-content" style={{ opacity: 0.7 }}>
              Please select a school
            </p>
          ) : (
          <div style={{ marginBottom: '24px' }}>
            <PillGroup
              size="lg"
              value={activeTab}
              options={[
                { value: 'school-info', label: 'School Information' },
                { value: 'embed-appearance', label: 'Embed Appearance' },
                // { value: 'featured-offers-in-person', label: 'Featured Offers (In-person)' },
                // { value: 'featured-offers-in-app', label: 'Featured Offers (In-app)' },
                { value: 'preferred-partners', label: 'Preferred Partners' },
                { value: 'offer-order-in-app', label: 'Offer Order (In-app)' },
                { value: 'initial-rewards', label: 'Initial Rewards' },
                { value: 'schedule', label: 'Schedule' },
              ]}
              onChange={(v) => setActiveTab(v)}
            />
          </div>
          )}

          {/* Error and Success Messages - Above Form Container */}
          {error && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              marginBottom: '24px',
              fontFamily: '"Baikal Book"',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{
              padding: '12px',
              backgroundColor: '#dcfce7',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              color: '#16a34a',
              marginBottom: '24px',
              fontFamily: '"Baikal Book"',
              fontSize: '14px'
            }}>
              {success}
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'school-info' && selectedSchool && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '32px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)'
            }}>
              <form onSubmit={handleSubmit}>
                {/* Logo Upload */}
                <div style={{ marginBottom: '32px' }}>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '12px'
                  }}>
                    School Logo
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    fontFamily: '"Baikal Book"',
                    marginBottom: '16px'
                  }}>
                    Upload a logo image for the school
                  </div>

                  {/* Show current/uploaded logo */}
                  {(formData.logoFile || formData.logoUrl) && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img
                          src={formData.logoFile ? URL.createObjectURL(formData.logoFile) : formData.logoUrl}
                          alt="School Logo"
                          style={{
                            maxWidth: '200px',
                            maxHeight: '100px',
                            objectFit: 'contain',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '8px',
                            backgroundColor: 'white'
                          }}
                        />
                        <button
                          type="button"
                          onClick={formData.logoFile ? removeLogo : null}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: formData.logoFile ? 'pointer' : 'default',
                            fontSize: '12px',
                            display: formData.logoFile ? 'block' : 'none'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Upload area */}
                  {!formData.logoFile && (
                    <div
                      style={{
                        border: '2px dashed #e5e7eb',
                        borderRadius: '8px',
                        padding: '24px',
                        textAlign: 'center',
                        backgroundColor: '#f9fafb'
                      }}
                      onDragOver={handleLogoDragOver}
                      onDrop={handleLogoDrop}
                    >
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoFileSelect}
                        style={{ display: 'none' }}
                      />
                      <div style={{ marginBottom: '8px', color: '#6b7280' }}>
                        Drag and drop image or click to browse
                      </div>
                      <button
                        type="button"
                        onClick={handleLogoUploadClick}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#000000',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        Choose Image
                      </button>
                    </div>
                  )}

                  {/* URL input fallback */}
                  {!formData.logoFile && (
                    <div style={{ marginTop: '16px' }}>
                      <input
                        type="url"
                        value={formData.logoUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, logoUrl: e.target.value }))}
                        placeholder="Or enter image URL: https://example.com/logo.png"
                        style={{
                          width: '100%',
                          maxWidth: '500px',
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Banner Upload */}
                <div style={{ marginBottom: '32px' }}>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '12px'
                  }}>
                    School Banner
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    fontFamily: '"Baikal Book"',
                    marginBottom: '16px'
                  }}>
                    Upload a banner image for the school dashboard
                  </div>

                  {/* Show current/uploaded banner */}
                  {(formData.bannerFile || formData.bannerUrl) && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img
                          src={formData.bannerFile ? URL.createObjectURL(formData.bannerFile) : formData.bannerUrl}
                          alt="School Banner"
                          style={{
                            maxWidth: '400px',
                            maxHeight: '150px',
                            objectFit: 'cover',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                        />
                        <button
                          type="button"
                          onClick={formData.bannerFile ? removeBanner : null}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: formData.bannerFile ? 'pointer' : 'default',
                            fontSize: '12px',
                            display: formData.bannerFile ? 'block' : 'none'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Upload area */}
                  {!formData.bannerFile && (
                    <div
                      style={{
                        border: '2px dashed #e5e7eb',
                        borderRadius: '8px',
                        padding: '24px',
                        textAlign: 'center',
                        backgroundColor: '#f9fafb'
                      }}
                      onDragOver={handleBannerDragOver}
                      onDrop={handleBannerDrop}
                    >
                      <input
                        ref={bannerInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleBannerFileSelect}
                        style={{ display: 'none' }}
                      />
                      <div style={{ marginBottom: '8px', color: '#6b7280' }}>
                        Drag and drop image or click to browse
                      </div>
                      <button
                        type="button"
                        onClick={handleBannerUploadClick}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#000000',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        Choose Image
                      </button>
                    </div>
                  )}

                  {/* URL input fallback */}
                  {!formData.bannerFile && (
                    <div style={{ marginTop: '16px' }}>
                      <input
                        type="url"
                        value={formData.bannerUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, bannerUrl: e.target.value }))}
                        placeholder="Or enter image URL: https://example.com/banner.png"
                        style={{
                          width: '100%',
                          maxWidth: '500px',
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '12px'
                  }}>
                    Points Banner Color
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    fontFamily: '"Baikal Book"',
                    marginBottom: '16px'
                  }}>
                    Background color for the "You've earned" points block on the mobile home screen
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <input
                      type="color"
                      value={toPickerValue(formData.primary_color)}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          primary_color: normalizeHexInput(e.target.value) || DEFAULT_POINTS_BANNER_COLOR,
                        }))
                      }
                      style={{
                        width: '48px',
                        height: '40px',
                        padding: 0,
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                      }}
                      aria-label="Points banner color"
                    />
                    <input
                      type="text"
                      name="primary_color"
                      value={formData.primary_color}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          primary_color: e.target.value,
                        }))
                      }
                      onBlur={() =>
                        setFormData((prev) => ({
                          ...prev,
                          primary_color:
                            normalizeHexInput(prev.primary_color) || DEFAULT_POINTS_BANNER_COLOR,
                        }))
                      }
                      placeholder="#001C44"
                      style={{
                        width: '140px',
                        padding: '10px 12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: '14px',
                      }}
                    />
                    <div
                      style={{
                        flex: 1,
                        minWidth: '220px',
                        maxWidth: '320px',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        backgroundColor: toPickerValue(formData.primary_color),
                        color: '#FFFFFF',
                        fontFamily: '"Baikal Book"',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>You've earned</div>
                        <div style={{ fontSize: '22px', fontWeight: 700, lineHeight: 1.1 }}>250 pts</div>
                      </div>
                      {(formData.logoFile || formData.logoUrl) ? (
                        <img
                          src={formData.logoFile ? URL.createObjectURL(formData.logoFile) : formData.logoUrl}
                          alt=""
                          style={{
                            width: '56px',
                            height: '56px',
                            objectFit: 'contain',
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Program Name */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#374151'
                  }}>
                    Program Name
                  </label>
                  <input
                    type="text"
                    name="rewards_title"
                    value={formData.rewards_title}
                    onChange={handleInputChange}
                    maxLength={100}
                    placeholder={deriveDefaultRewardsTitle(selectedSchool) || 'e.g. Utah Rewards'}
                    style={{
                      width: '320px',
                      maxWidth: '100%',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6b7280',
                    fontFamily: '"Baikal Book"'
                  }}>
                    Shown in the app wherever the program is named — activity rows, the
                    browser extension page and the walkthrough. Leave blank to use
                    "{deriveDefaultRewardsTitle(selectedSchool) || 'Mascot Rewards'}".
                  </p>
                </div>

                {/* Commission Split */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#374151'
                  }}>
                    Commission Split (%)
                  </label>
                  <input
                    type="number"
                    name="commission_split"
                    value={formData.commission_split}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    style={{
                      width: '200px',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6b7280',
                    fontFamily: '"Baikal Book"'
                  }}>
                    Percentage of commission that goes to the school (0-100)
                  </p>
                </div>

                {/* Content Active Toggle */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      name="content_active"
                      checked={formData.content_active}
                      onChange={handleInputChange}
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{
                      fontFamily: '"Baikal Book"',
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      Enable Content Tab
                    </span>
                  </label>
                  <p style={{
                    marginTop: '8px',
                    marginLeft: '32px',
                    fontSize: '12px',
                    color: '#6b7280',
                    fontFamily: '"Baikal Book"'
                  }}>
                    Show the Content tab in navigation for this school
                  </p>
                </div>

                {/* Travel Active Toggle */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      name="travel_active"
                      checked={formData.travel_active}
                      onChange={handleInputChange}
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{
                      fontFamily: '"Baikal Book"',
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      Enable Travel Tab
                    </span>
                  </label>
                  <p style={{
                    marginTop: '8px',
                    marginLeft: '32px',
                    fontSize: '12px',
                    color: '#6b7280',
                    fontFamily: '"Baikal Book"'
                  }}>
                    Show the Travel tab in navigation for this school
                  </p>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      name="is_stripe_feature_enabled"
                      checked={formData.is_stripe_feature_enabled}
                      onChange={handleInputChange}
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{
                      fontFamily: '"Baikal Book"',
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      Stripe card purchases (USD rewards)
                    </span>
                  </label>
                  <p style={{
                    marginTop: '8px',
                    marginLeft: '32px',
                    fontSize: '12px',
                    color: '#6b7280',
                    fontFamily: '"Baikal Book"'
                  }}>
                    When off, users do not see card-only rewards or dollar prices on first-come rewards
                  </p>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <span style={{
                    display: 'block',
                    marginBottom: '12px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#374151'
                  }}>
                    Browser extension
                  </span>
                  {[
                    { value: 'none', label: 'No extension', hint: 'Turns off Safari setup and the extension link in embed. Also skips extension onboarding and rewards.' },
                    { value: 'homecrowd', label: 'Homecrowd extension', hint: 'Keeps Safari setup and rewards in the Homecrowd app. Turns off Safari setup and the extension link in embed.' },
                    { value: 'built_in', label: 'Built into the school app', hint: 'Turns on Safari setup and the extension link in embed. Detects install from the school app.' },
                    { value: 'separate_app', label: 'Separate extension app', hint: 'Turns on Safari setup in embed and an Apple link to the standalone app.' },
                  ].map((option) => (
                    <label
                      key={option.value}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        cursor: 'pointer',
                        marginBottom: '12px'
                      }}
                    >
                      <input
                        type="radio"
                        name="extension_mode"
                        value={option.value}
                        checked={formData.extension_mode === option.value}
                        onChange={handleInputChange}
                        style={{
                          width: '18px',
                          height: '18px',
                          marginRight: '12px',
                          marginTop: '2px',
                          cursor: 'pointer'
                        }}
                      />
                      <span>
                        <span style={{
                          display: 'block',
                          fontFamily: '"Baikal Book"',
                          fontSize: '15px',
                          fontWeight: 500,
                          color: '#374151'
                        }}>
                          {option.label}
                        </span>
                        <span style={{
                          display: 'block',
                          marginTop: '2px',
                          fontSize: '12px',
                          color: '#6b7280',
                          fontFamily: '"Baikal Book"'
                        }}>
                          {option.hint}
                        </span>
                      </span>
                    </label>
                  ))}
                  {formData.extension_mode === 'separate_app' ? (
                    <div style={{ marginTop: '8px', marginLeft: '30px' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        Extension app page (Apple)
                      </label>
                      <input
                        type="url"
                        name="extension_app_url"
                        value={formData.extension_app_url}
                        onChange={handleInputChange}
                        placeholder="https://example.com/get-extension"
                        style={{
                          width: '420px',
                          maxWidth: '100%',
                          padding: '12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px'
                        }}
                      />
                      <p style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#6b7280',
                        fontFamily: '"Baikal Book"'
                      }}>
                        Landing page where Apple users can open the standalone extension app.
                      </p>
                    </div>
                  ) : null}
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{
                      fontFamily: '"Baikal Book"',
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      Active
                    </span>
                  </label>
                  <p style={{
                    marginTop: '8px',
                    marginLeft: '32px',
                    fontSize: '12px',
                    color: '#6b7280',
                    fontFamily: '"Baikal Book"'
                  }}>
                    When Active, the school will be available for selection during registration.
                  </p>
                </div>

                {/* Submit Button */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: saving ? '#9ca3af' : '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => !saving && (e.target.style.backgroundColor = '#1f2937')}
                    onMouseOut={(e) => !saving && (e.target.style.backgroundColor = '#000000')}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate('/superuser/dashboard/')}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'white',
                      color: '#374151',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
                    onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Featured Offers Tab (split: in-person = card_linked, in-app = click).
              Its two pills are commented out above, so this panel is unreachable —
              the guard below can no longer be true. Left in place rather than
              deleted so the tabs can be restored by uncommenting them. */}
          {(activeTab === 'featured-offers-in-person' || activeTab === 'featured-offers-in-app') && selectedSchool && (() => {
            const currentOfferType = activeTab === 'featured-offers-in-app' ? 'click' : 'card_linked';
            const currentOfferTypeLabel = activeTab === 'featured-offers-in-app' ? 'In-app' : 'In-person';
            const visibleOffers = featuredOffers.filter(
              (o) =>
                (o.offer_type || 'card_linked') === currentOfferType &&
                !o.is_preferred_partner
            );
            return (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '32px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)'
            }}>
              <h3 style={{
                fontFamily: '"Baikal Book"',
                fontSize: '20px',
                fontWeight: 600,
                color: '#111827',
                marginBottom: '24px'
              }}>
                Featured Offers ({currentOfferTypeLabel}) for {selectedSchool.name}
              </h3>

              {/* Add New Offer Form */}
              {/* Existing Offers List */}
              <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                marginBottom: '32px',
                border: '1px solid #e5e7eb'
              }}>
                <h4 style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '20px',
                  color: '#111827'
                }}>
                  Current Featured Offers
                </h4>

                {loadingOffers ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    Loading offers...
                  </div>
                ) : visibleOffers.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    color: '#6b7280'
                  }}>
                    No featured offers for this school yet.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {visibleOffers
                          .slice()
                          .sort((a, b) =>
                            ((b.top_order || 0) + (b.bottom_order || 0)) -
                            ((a.top_order || 0) + (a.bottom_order || 0))
                          )
                          .map((offer) => {
                            const statusLabels = [];
                            if (offer.school == null) statusLabels.push({ label: 'Global', kind: 'global' });
                            if (offer.top_featured) statusLabels.push({ label: `Top · order ${offer.top_order || 0}`, kind: 'featured' });
                            if (offer.bottom_featured) statusLabels.push({ label: `Bottom · order ${offer.bottom_order || 0}`, kind: 'featured' });
                            if (!offer.is_active) statusLabels.push({ label: 'Inactive', kind: 'inactive' });
                            return (
                              <div
                                key={offer.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '16px',
                                  backgroundColor: '#f9fafb',
                                  borderRadius: '8px',
                                  gap: '16px',
                                  opacity: offer.is_active ? 1 : 0.6
                                }}
                              >
                                {offer.large_logo_url && (
                                  <img
                                    src={offer.large_logo_url}
                                    alt={offer.name}
                                    style={{
                                      width: '50px',
                                      height: '50px',
                                      objectFit: 'contain',
                                      borderRadius: '4px'
                                    }}
                                  />
                                )}
                                {offer.small_logo_url && (
                                  <img
                                    src={offer.small_logo_url}
                                    alt={`${offer.name} badge`}
                                    style={{
                                      width: '36px',
                                      height: '36px',
                                      objectFit: 'cover',
                                      borderRadius: '999px',
                                      border: '1px solid #e5e7eb',
                                      backgroundColor: 'white'
                                    }}
                                  />
                                )}
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    fontFamily: '"Baikal Book"',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    marginBottom: '4px'
                                  }}>
                                    {offer.name}
                                  </div>
                                  <div style={{
                                    fontFamily: '"Baikal Book"',
                                    fontSize: '12px',
                                    color: '#6b7280',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}>
                                    <span>ID: {offer.offer_id}</span>
                                    {statusLabels.map(({ label, kind }) => {
                                      const palette = kind === 'inactive'
                                        ? { bg: '#fee2e2', fg: '#991b1b' }
                                        : kind === 'global'
                                          ? { bg: '#ede9fe', fg: '#5b21b6' }
                                          : { bg: '#e0f7ff', fg: '#0369a1' };
                                      return (
                                        <span
                                          key={label}
                                          style={{
                                            padding: '2px 8px',
                                            backgroundColor: palette.bg,
                                            color: palette.fg,
                                            borderRadius: '999px',
                                            fontSize: '11px',
                                            fontWeight: 500
                                          }}
                                        >
                                          {label}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="dropdown-menu" style={{ position: 'relative' }}>
                                  <button
                                    onClick={() => setActiveDropdown(activeDropdown === `inapp-${offer.id}` ? null : `inapp-${offer.id}`)}
                                    style={{
                                      padding: '4px',
                                      backgroundColor: 'transparent',
                                      color: '#6b7280',
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontFamily: '"Baikal Book"',
                                      fontSize: '24px',
                                      fontWeight: 'bold',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '32px',
                                      height: '32px'
                                    }}
                                  >
                                    ⋮
                                  </button>
                                  {activeDropdown === `inapp-${offer.id}` && (
                                    <div style={{
                                      position: 'absolute',
                                      top: '100%',
                                      right: 0,
                                      marginTop: '4px',
                                      backgroundColor: 'white',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '8px',
                                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                      zIndex: 1000,
                                      minWidth: '150px'
                                    }}>
                                      <button
                                        onClick={() => handleEditOffer(offer)}
                                        style={{
                                          display: 'block',
                                          width: '100%',
                                          padding: '8px 12px',
                                          backgroundColor: 'transparent',
                                          color: '#374151',
                                          border: 'none',
                                          fontFamily: '"Baikal Book"',
                                          fontSize: '14px',
                                          cursor: 'pointer',
                                          textAlign: 'left'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => offer.is_active ? handleDeactivateOffer(offer, offer.top_featured ? 'top' : 'bottom') : handleActivateOffer(offer.id)}
                                        style={{
                                          display: 'block',
                                          width: '100%',
                                          padding: '8px 12px',
                                          backgroundColor: 'transparent',
                                          color: '#374151',
                                          border: 'none',
                                          fontFamily: '"Baikal Book"',
                                          fontSize: '14px',
                                          cursor: 'pointer',
                                          textAlign: 'left',
                                          borderTop: '1px solid #e5e7eb'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                      >
                                        {offer.is_active ? 'Deactivate' : 'Activate'}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                  </div>
                )}

                {/* Add Offer Button */}
                <button
                  onClick={() => {
                    setEditingOffer(null);
                    setNewOffer({
                      name: '',
                      offer_id: '',
                      top_order: 0,
                      bottom_order: 0,
                      top_featured: false,
                      bottom_featured: false,
                      small_logo_url: '',
                      large_logo_url: '',
                      small_logo_file: null,
                      large_logo_file: null,
                      summary: '',
                      is_active: true,
                      is_global: false
                    });
                    setShowAddForm(true);
                  }}
                  style={{
                    marginTop: '20px',
                    padding: '12px 24px',
                    backgroundColor: '#00C8FF',
                    color: '#000000',
                    border: 'none',
                    borderRadius: '8px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#00b3e6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#00C8FF'}
                >
                  <span style={{ fontSize: '18px', lineHeight: '1' }}>+</span>
                  Add New Offer
                </button>
              </div>

              {/* Add / Edit Featured Offer Modal */}
              {showAddForm && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  backgroundColor: 'rgba(0,0,0,0.45)',
                  zIndex: 1002,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '24px'
                }}
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) handleCloseOfferModal();
                }}
              >
              <div style={{
                width: '100%',
                maxWidth: '720px',
                maxHeight: 'calc(100vh - 48px)',
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 18px',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '16px',
                    fontWeight: 900,
                    color: '#111827'
                  }}>
                    {editingOffer ? 'Edit Featured Offer' : 'Add New Featured Offer'}
                  </div>
                  <button
                    onClick={handleCloseOfferModal}
                    disabled={saving}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      lineHeight: 1,
                      padding: '6px 8px',
                      borderRadius: '8px',
                      color: '#111827',
                      opacity: saving ? 0.6 : 1
                    }}
                  >
                    ×
                  </button>
                </div>
                <div style={{ padding: '24px', overflowY: 'auto', flex: 1, minHeight: 0 }}>

                {error && (
                  <div style={{
                    padding: '12px 14px',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    color: '#991b1b',
                    marginBottom: '20px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '12px'
                  }}>
                    <span style={{ flex: 1 }}>{error}</span>
                    <button
                      type="button"
                      onClick={() => setError(null)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#991b1b',
                        cursor: 'pointer',
                        fontSize: '18px',
                        lineHeight: 1,
                        padding: 0
                      }}
                      aria-label="Dismiss error"
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Basic Info */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>Offer Name *</label>
                    <input
                      type="text"
                      value={newOffer.name}
                      onChange={(e) => setNewOffer(prev => ({ ...prev, name: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>{currentOfferType === 'click' ? 'Wildfire Offer ID *' : 'Olive Offer ID *'}</label>
                    <input
                      type="text"
                      value={newOffer.offer_id}
                      onChange={(e) => setNewOffer(prev => ({ ...prev, offer_id: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                  </div>
                </div>

                {/* Summary */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151'
                  }}>Summary</label>
                  <textarea
                    value={newOffer.summary}
                    onChange={(e) => setNewOffer(prev => ({ ...prev, summary: e.target.value }))}
                    rows="3"
                    placeholder="Brief description of the offer"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* Bottom Featured Section */}
                <div style={{
                  marginBottom: '24px',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '16px' }}>
                    <input
                      type="checkbox"
                      checked={newOffer.bottom_featured}
                      onChange={(e) => setNewOffer(prev => ({ ...prev, bottom_featured: e.target.checked }))}
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '8px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{
                      fontFamily: '"Baikal Book"',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#111827'
                    }}>Bottom Featured</span>
                  </label>

                  {newOffer.bottom_featured && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#374151'
                        }}>Bottom Order</label>
                        <input
                          type="number"
                          value={newOffer.bottom_order}
                          onChange={(e) => setNewOffer(prev => ({ ...prev, bottom_order: parseInt(e.target.value) || 0 }))}
                          placeholder="Higher = earlier in list"
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontFamily: '"Baikal Book"',
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Top Featured Section */}
                <div style={{
                  marginBottom: '24px',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '16px' }}>
                    <input
                      type="checkbox"
                      checked={newOffer.top_featured}
                      onChange={(e) => setNewOffer(prev => ({ ...prev, top_featured: e.target.checked }))}
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '8px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{
                      fontFamily: '"Baikal Book"',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#111827'
                    }}>Top Featured</span>
                  </label>

                  {newOffer.top_featured && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#374151'
                        }}>Top Order</label>
                        <input
                          type="number"
                          value={newOffer.top_order}
                          onChange={(e) => setNewOffer(prev => ({ ...prev, top_order: parseInt(e.target.value) || 0 }))}
                          placeholder="Higher = earlier in list"
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontFamily: '"Baikal Book"',
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Logos */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '24px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>Large Logo</label>
                    {(newOffer.large_logo_file || newOffer.large_logo_url) ? (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img
                          src={newOffer.large_logo_file ? URL.createObjectURL(newOffer.large_logo_file) : newOffer.large_logo_url}
                          alt="Large Logo"
                          style={{
                            width: '285px',
                            height: '285px',
                            objectFit: 'cover',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            backgroundColor: 'white'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setNewOffer(prev => ({ ...prev, large_logo_file: null, large_logo_url: '' }))}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div
                        style={{
                          border: '2px dashed #e5e7eb',
                          borderRadius: '8px',
                          padding: '20px',
                          textAlign: 'center',
                          backgroundColor: 'white'
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const files = e.dataTransfer.files;
                          if (files && files[0] && files[0].type.startsWith('image/')) {
                            setNewOffer(prev => ({ ...prev, large_logo_file: files[0] }));
                          }
                        }}
                      >
                        <input
                          ref={largeLogoInputRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file && file.type.startsWith('image/')) {
                              setNewOffer(prev => ({ ...prev, large_logo_file: file }));
                            }
                          }}
                          style={{ display: 'none' }}
                        />
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                          Drag and drop or click
                        </div>
                        <button
                          type="button"
                          onClick={() => largeLogoInputRef.current?.click()}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#000000',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontFamily: '"Baikal Book"',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Choose Image
                        </button>
                      </div>
                    )}
                  </div>
                  {currentOfferType !== 'click' && (
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>Small Logo</label>
                      {(newOffer.small_logo_file || newOffer.small_logo_url) ? (
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <img
                            src={newOffer.small_logo_file ? URL.createObjectURL(newOffer.small_logo_file) : newOffer.small_logo_url}
                            alt="Small Logo"
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover',
                              border: '1px solid #e5e7eb',
                              borderRadius: '25px',
                              backgroundColor: 'white'
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => setNewOffer(prev => ({ ...prev, small_logo_file: null, small_logo_url: '' }))}
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div
                          style={{
                            border: '2px dashed #e5e7eb',
                            borderRadius: '8px',
                            padding: '20px',
                            textAlign: 'center',
                            backgroundColor: 'white'
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const files = e.dataTransfer.files;
                            if (files && files[0] && files[0].type.startsWith('image/')) {
                              setNewOffer(prev => ({ ...prev, small_logo_file: files[0] }));
                            }
                          }}
                        >
                          <input
                            ref={smallLogoInputRef}
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file && file.type.startsWith('image/')) {
                                setNewOffer(prev => ({ ...prev, small_logo_file: file }));
                              }
                            }}
                            style={{ display: 'none' }}
                          />
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                            Drag and drop or click
                          </div>
                          <button
                            type="button"
                            onClick={() => smallLogoInputRef.current?.click()}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#000000',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontFamily: '"Baikal Book"',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Choose Image
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Active Status & Global Scope */}
                <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newOffer.is_active}
                      onChange={(e) => setNewOffer(prev => ({ ...prev, is_active: e.target.checked }))}
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '8px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      color: '#374151'
                    }}>Active</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newOffer.is_global}
                      onChange={(e) => setNewOffer(prev => ({ ...prev, is_global: e.target.checked }))}
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '8px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      color: '#374151'
                    }}>
                      Global — show to all schools
                      {selectedSchool && !newOffer.is_global && (
                        <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '6px' }}>
                          (scoped to {selectedSchool.name})
                        </span>
                      )}
                    </span>
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleCloseOfferModal}
                    disabled={saving}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'white',
                      color: '#374151',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      opacity: saving ? 0.6 : 1,
                      transition: 'all 0.2s'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddOffer}
                    disabled={saving}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: saving ? '#9ca3af' : '#00C8FF',
                      color: saving ? 'white' : '#000000',
                      border: 'none',
                      borderRadius: '8px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => !saving && (e.target.style.backgroundColor = '#00b3e6')}
                    onMouseOut={(e) => !saving && (e.target.style.backgroundColor = '#00C8FF')}
                  >
                    {saving ? (editingOffer ? 'Updating...' : 'Adding...') : (editingOffer ? 'Update Offer' : 'Add Offer')}
                  </button>
                </div>
              </div>
              </div>
              </div>
              )}
            </div>
            );
          })()}

          {activeTab === 'offer-order-in-app' && selectedSchool && (() => {
            const { sorted, pinnedPreview } = offerOrderDerived;
            const dirtyCount = Object.keys(offerOrderDirty).length;

            const stageEdit = (mid, patch) => {
              setOfferOrderDirty((prev) => {
                const original = offerOrderMerchants.find((x) => x.wildfire_merchant_id === mid);
                const originalOrder = original?.order_number ?? null;
                const originalGlobal = !!original?.is_global;
                const current = prev[mid] || { order_number: originalOrder, is_global: originalGlobal };
                const next = { ...current, ...patch };
                // Drop dirty entry if it matches the original
                if (next.order_number === originalOrder && next.is_global === originalGlobal) {
                  const cleaned = { ...prev };
                  delete cleaned[mid];
                  return cleaned;
                }
                return { ...prev, [mid]: next };
              });
              setOfferOrderSuccess(null);
            };

            const handleOrderChange = (mid, raw) => {
              const trimmed = (raw || '').trim();
              const parsed = trimmed === '' ? null : Number.parseInt(trimmed, 10);
              const value = trimmed === '' || Number.isNaN(parsed) ? null : parsed;
              stageEdit(mid, { order_number: value });
            };

            const handleGlobalToggle = (mid, isGlobalNow) => {
              stageEdit(mid, { is_global: !!isGlobalNow });
            };

            const handleSave = async () => {
              const entries = Object.entries(offerOrderDirty).map(([mid, val]) => ({
                wildfire_merchant_id: Number.parseInt(mid, 10),
                order_number: val.order_number,
                is_global: !!val.is_global,
              }));
              // Warn before any destructive global delete (affects every school).
              const globalDeletes = entries.filter((e) => e.order_number === null && e.is_global);
              if (globalDeletes.length > 0) {
                const ok = window.confirm(
                  `This will remove ${globalDeletes.length} global rule${globalDeletes.length === 1 ? '' : 's'} affecting every school. Continue?`,
                );
                if (!ok) return;
              }
              setOfferOrderSaving(true);
              setOfferOrderError(null);
              setOfferOrderSuccess(null);
              try {
                await apiService.saveWildfireOfferOrder({ schoolId: selectedSchool.id, entries });
                const refreshed = await apiService.getWildfireOfferOrder({ schoolId: selectedSchool.id });
                setOfferOrderMerchants(refreshed.results || []);
                setOfferOrderDirty({});
                setOfferOrderSuccess(`Saved ${entries.length} change${entries.length === 1 ? '' : 's'}.`);
              } catch (err) {
                setOfferOrderError(err?.response?.data?.error || err?.message || 'Failed to save offer order');
              } finally {
                setOfferOrderSaving(false);
              }
            };

            const handleDiscard = () => {
              if (dirtyCount === 0) return;
              const ok = window.confirm(`Discard ${dirtyCount} unsaved change${dirtyCount === 1 ? '' : 's'}?`);
              if (!ok) return;
              setOfferOrderDirty({});
              setOfferOrderSuccess(null);
              setOfferOrderError(null);
            };

            return (
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '32px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)' }}>
                <h3 style={{ fontFamily: '"Baikal Book"', fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                  Offer Order (In-app)
                </h3>
                <p style={{ fontFamily: '"Baikal Book"', fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                  Pin Wildfire offers to the top of the in-app offers list for {selectedSchool.name}. Higher numbers float higher.
                  Leave the field blank to unpin. Check "Global" to apply the rule to every school (school-specific overrides are removed when promoting).
                </p>

                <div style={{ marginBottom: '16px' }}>
                  <PillGroup
                    size="md"
                    value={offerOrderView}
                    options={[
                      { value: 'edit', label: 'Edit' },
                      { value: 'preview', label: `Preview top (${pinnedPreview.length})` },
                    ]}
                    onChange={(v) => setOfferOrderView(v)}
                  />
                </div>

                {offerOrderError && (
                  <div style={{ padding: '12px', backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontFamily: '"Baikal Book"', fontSize: '14px' }}>
                    {offerOrderError}
                  </div>
                )}
                {offerOrderSuccess && (
                  <div style={{ padding: '12px', backgroundColor: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', marginBottom: '16px', fontFamily: '"Baikal Book"', fontSize: '14px' }}>
                    {offerOrderSuccess}
                  </div>
                )}

                {offerOrderView === 'edit' && (
                  <>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
                      <input
                        type="text"
                        placeholder="Search merchants..."
                        value={offerOrderSearch}
                        onChange={(e) => setOfferOrderSearch(e.target.value)}
                        style={{ flex: 1, padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', fontFamily: '"Baikal Book"' }}
                      />
                      <button
                        type="button"
                        onClick={handleDiscard}
                        disabled={dirtyCount === 0 || offerOrderSaving}
                        style={{
                          padding: '10px 16px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          backgroundColor: 'white',
                          color: dirtyCount === 0 || offerOrderSaving ? '#9ca3af' : '#374151',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          cursor: dirtyCount === 0 || offerOrderSaving ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Discard
                      </button>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={dirtyCount === 0 || offerOrderSaving}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '8px',
                          border: 'none',
                          backgroundColor: dirtyCount === 0 || offerOrderSaving ? '#9ca3af' : '#111827',
                          color: 'white',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          cursor: dirtyCount === 0 || offerOrderSaving ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {offerOrderSaving ? 'Saving…' : `Save${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`}
                      </button>
                    </div>

                    {offerOrderLoading ? (
                      <p style={{ fontFamily: '"Baikal Book"', fontSize: '14px', color: '#6b7280' }}>Loading merchants…</p>
                    ) : sorted.length === 0 ? (
                      <p style={{ fontFamily: '"Baikal Book"', fontSize: '14px', color: '#6b7280' }}>No merchants match your search.</p>
                    ) : (
                      <div style={{ maxHeight: '600px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        {sorted.map((m, idx) => {
                          const isPinned = m.effective_order != null;
                          const isDirty = Object.prototype.hasOwnProperty.call(offerOrderDirty, m.wildfire_merchant_id);
                          return (
                            <div
                              key={m.wildfire_merchant_id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '10px 14px',
                                borderBottom: idx === sorted.length - 1 ? 'none' : '1px solid #f3f4f6',
                                backgroundColor: isPinned ? '#fffbeb' : 'white',
                              }}
                            >
                              {m.logo_url ? (
                                <img src={m.logo_url} alt="" style={{ width: '32px', height: '32px', objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }} />
                              ) : (
                                <div style={{ width: '32px', height: '32px', borderRadius: '4px', backgroundColor: '#f3f4f6', flexShrink: 0 }} />
                              )}
                              <div style={{ flex: 1, minWidth: 0, fontFamily: '"Baikal Book"', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {m.name || `Merchant ${m.wildfire_merchant_id}`}
                              </div>
                              <span style={{ fontFamily: '"Baikal Book"', fontSize: '12px', color: '#9ca3af', flexShrink: 0 }}>
                                ID {m.wildfire_merchant_id}
                              </span>
                              <input
                                type="number"
                                placeholder="—"
                                value={m.effective_order ?? ''}
                                onChange={(e) => handleOrderChange(m.wildfire_merchant_id, e.target.value)}
                                style={{
                                  width: '90px',
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  border: `1px solid ${isDirty ? '#f59e0b' : '#e5e7eb'}`,
                                  fontSize: '14px',
                                  fontFamily: '"Baikal Book"',
                                  textAlign: 'right',
                                  flexShrink: 0,
                                }}
                              />
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontFamily: '"Baikal Book"', fontSize: '12px', color: '#374151', flexShrink: 0, cursor: 'pointer', minWidth: '70px' }}>
                                <input
                                  type="checkbox"
                                  checked={!!m.effective_is_global}
                                  onChange={(e) => handleGlobalToggle(m.wildfire_merchant_id, e.target.checked)}
                                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                                Global
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {offerOrderView === 'preview' && (
                  <div>
                    <p style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                      This is what a user at {selectedSchool.name} would see at the top of the in-app Wildfire offers list (unsaved edits included).
                      Unpinned merchants follow below in alphabetical order.
                    </p>
                    {pinnedPreview.length === 0 ? (
                      <p style={{ fontFamily: '"Baikal Book"', fontSize: '14px', color: '#6b7280' }}>No pinned offers yet. Pin some merchants in the Edit tab.</p>
                    ) : (
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        {pinnedPreview.map((m, idx) => (
                          <div
                            key={m.wildfire_merchant_id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '10px 14px',
                              borderBottom: idx === pinnedPreview.length - 1 ? 'none' : '1px solid #f3f4f6',
                              backgroundColor: '#fffbeb',
                            }}
                          >
                            <span style={{ width: '28px', textAlign: 'right', fontFamily: '"Baikal Book"', fontSize: '13px', color: '#9ca3af', flexShrink: 0 }}>
                              #{idx + 1}
                            </span>
                            {m.logo_url ? (
                              <img src={m.logo_url} alt="" style={{ width: '32px', height: '32px', objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }} />
                            ) : (
                              <div style={{ width: '32px', height: '32px', borderRadius: '4px', backgroundColor: '#f3f4f6', flexShrink: 0 }} />
                            )}
                            <div style={{ flex: 1, minWidth: 0, fontFamily: '"Baikal Book"', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {m.name || `Merchant ${m.wildfire_merchant_id}`}
                            </div>
                            <span style={{ fontFamily: '"Baikal Book"', fontSize: '12px', color: '#6b7280', flexShrink: 0 }}>
                              order {m.effective_order}{m.effective_is_global ? ' · global' : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {activeTab === 'preferred-partners' && selectedSchool && (
            <PreferredPartnersSection
              schoolId={selectedSchool.id}
              schoolName={selectedSchool.name}
              key={selectedSchool.id}
            />
          )}

          {activeTab === 'initial-rewards' && selectedSchool && (
            <FirstRewardsSection
              schoolId={selectedSchool.id}
              schoolName={selectedSchool.name}
              key={selectedSchool.id}
            />
          )}

          {activeTab === 'schedule' && selectedSchool && (
            <ScheduleSection
              schoolId={selectedSchool.id}
              schoolName={selectedSchool.name}
              schools={schools}
              key={selectedSchool.id}
            />
          )}

          {activeTab === 'embed-appearance' && selectedSchool && (
            <EmbedAppearanceSection schoolId={selectedSchool.id} key={selectedSchool.id} />
          )}

          {activeTab === 'leaderboard' && selectedSchool && (
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '32px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
              }}
            >
              <h3
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '8px',
                }}
              >
                Weekly leaderboard — {selectedSchool.name}
              </h3>
              <p
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '13px',
                  color: '#6b7280',
                  marginBottom: '24px',
                  lineHeight: 1.5,
                }}
              >
                <strong>Show leaderboard</strong> turns the in-app weekly leaderboard on or off for this school.
                <strong> Custom schedule &amp; filters</strong> — when enabled, the timezone, weekday, and time below
                are used; otherwise the platform default applies (Saturday 4:00 PM America/Denver, 7-day window).
              </p>
              {winnerSnapshotError && (
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#991b1b',
                    fontFamily: '"Baikal Book"',
                    fontSize: '13px',
                  }}
                >
                  {winnerSnapshotError}
                </div>
              )}
              {winnerSnapshot && (
                <div
                  style={{
                    marginBottom: '20px',
                    padding: '16px 18px',
                    borderRadius: '10px',
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    fontFamily: '"Baikal Book"',
                    fontSize: '13px',
                    color: '#0c4a6e',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '10px', color: '#075985' }}>
                    Last completed period winner (superuser lookup)
                  </div>
                  {!winnerSnapshot.leaderboard_active && (
                    <div style={{ color: '#0369a1' }}>
                      Leaderboard is off for this school — no winner snapshot for the hidden period.
                    </div>
                  )}
                  {winnerSnapshot.leaderboard_active && !winnerSnapshot.winner && (
                    <div style={{ color: '#0369a1' }}>
                      No winner for the previous scoring window (no qualifying points).
                    </div>
                  )}
                  {winnerSnapshot.leaderboard_active && winnerSnapshot.winner && (
                    <dl style={{ margin: 0 }}>
                      <dt style={{ marginTop: '8px', fontWeight: 600 }}>Email (Users admin search)</dt>
                      <dd
                        style={{
                          margin: '4px 0 0 0',
                          fontFamily: 'ui-monospace, monospace',
                          wordBreak: 'break-all',
                        }}
                      >
                        {winnerSnapshot.winner.email || '—'}
                      </dd>
                      <dt style={{ marginTop: '10px', fontWeight: 600 }}>User ID</dt>
                      <dd style={{ margin: '4px 0 0 0', fontFamily: 'ui-monospace, monospace' }}>
                        {winnerSnapshot.winner.id}
                      </dd>
                      <dt style={{ marginTop: '10px', fontWeight: 600 }}>Fan ID</dt>
                      <dd style={{ margin: '4px 0 0 0', fontFamily: 'ui-monospace, monospace' }}>
                        {winnerSnapshot.winner.fan_id != null && winnerSnapshot.winner.fan_id !== ''
                          ? winnerSnapshot.winner.fan_id
                          : '—'}
                      </dd>
                      <dt style={{ marginTop: '10px', fontWeight: 600 }}>Name (public)</dt>
                      <dd style={{ margin: '4px 0 0 0' }}>{winnerSnapshot.winner.name || '—'}</dd>
                      <dt style={{ marginTop: '10px', fontWeight: 600 }}>Points in window</dt>
                      <dd style={{ margin: '4px 0 0 0' }}>{winnerSnapshot.winner.points}</dd>
                      <dt style={{ marginTop: '10px', fontWeight: 600 }}>Window scored</dt>
                      <dd
                        style={{
                          margin: '4px 0 0 0',
                          fontFamily: 'ui-monospace, monospace',
                          fontSize: '12px',
                        }}
                      >
                        {winnerSnapshot.winner.week_started_at} → {winnerSnapshot.winner.week_ended_at}
                      </dd>
                    </dl>
                  )}
                  {winnerSnapshot.week_current_start != null && (
                    <div style={{ marginTop: '12px', fontSize: '12px', color: '#075985', opacity: 0.9 }}>
                      Current week starts: {winnerSnapshot.week_current_start}
                      {winnerSnapshot.scoring_period_days != null &&
                        ` · period length ${winnerSnapshot.scoring_period_days} day(s)`}
                    </div>
                  )}
                </div>
              )}
              {leaderboardLoading || !leaderboardForm ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>Loading…</div>
              ) : (
                <form onSubmit={handleLeaderboardSave}>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
                    {leaderboardForm.has_saved_config
                      ? 'Saved settings exist for this school.'
                      : 'No database row yet — defaults are shown; Save will create the configuration.'}
                  </p>

                  <div style={{ marginBottom: '18px' }}>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                      }}
                    >
                      Show leaderboard
                    </label>
                    <select
                      name="leaderboard_active"
                      value={leaderboardForm.leaderboard_active ? 'true' : 'false'}
                      onChange={handleLeaderboardFieldChange}
                      style={lbSelectStyle}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No (hide for this school)</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '18px' }}>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                      }}
                    >
                      Custom schedule &amp; point filters
                    </label>
                    <select
                      name="enabled"
                      value={leaderboardForm.enabled ? 'true' : 'false'}
                      onChange={handleLeaderboardFieldChange}
                      style={lbSelectStyle}
                    >
                      <option value="false">No (platform default)</option>
                      <option value="true">Yes (use fields below)</option>
                    </select>
                  </div>

                  <div
                    style={{
                      marginBottom: '18px',
                      opacity: leaderboardForm.enabled ? 1 : 0.5,
                      pointerEvents: leaderboardForm.enabled ? 'auto' : 'none',
                    }}
                  >
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                      }}
                    >
                      Time zone
                    </label>
                    <select
                      name="timezone"
                      disabled={!leaderboardForm.enabled}
                      value={leaderboardForm.timezone || 'America/Denver'}
                      onChange={handleLeaderboardFieldChange}
                      style={lbSelectStyle}
                    >
                      {timezoneSelectOptions(leaderboardForm.timezone).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                      gap: '16px',
                      marginBottom: '18px',
                      opacity: leaderboardForm.enabled ? 1 : 0.5,
                      pointerEvents: leaderboardForm.enabled ? 'auto' : 'none',
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#374151',
                        }}
                      >
                        Week rollover day
                      </label>
                      <select
                        name="week_boundary_weekday"
                        disabled={!leaderboardForm.enabled}
                        value={Number(leaderboardForm.week_boundary_weekday)}
                        onChange={handleLeaderboardFieldChange}
                        style={lbSelectStyle}
                      >
                        {LB_WEEKDAY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#374151',
                        }}
                      >
                        Hour (12-hour)
                      </label>
                      <select
                        name="boundary_hour_12"
                        disabled={!leaderboardForm.enabled}
                        value={Number(leaderboardForm.boundary_hour_12)}
                        onChange={handleLeaderboardFieldChange}
                        style={lbSelectStyle}
                      >
                        {LB_HOUR12_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#374151',
                        }}
                      >
                        AM / PM
                      </label>
                      <select
                        name="boundary_ampm"
                        disabled={!leaderboardForm.enabled}
                        value={leaderboardForm.boundary_ampm || 'PM'}
                        onChange={handleLeaderboardFieldChange}
                        style={lbSelectStyle}
                      >
                        {LB_AMPM_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#374151',
                        }}
                      >
                        Minutes (5-minute steps)
                      </label>
                      <select
                        name="week_boundary_minute"
                        disabled={!leaderboardForm.enabled}
                        value={Number(leaderboardForm.week_boundary_minute)}
                        onChange={handleLeaderboardFieldChange}
                        style={lbSelectStyle}
                      >
                        {LB_MINUTE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#374151',
                        }}
                      >
                        Scoring period length
                      </label>
                      <select
                        name="scoring_period_days"
                        disabled={!leaderboardForm.enabled}
                        value={Number(leaderboardForm.scoring_period_days)}
                        onChange={handleLeaderboardFieldChange}
                        style={lbSelectStyle}
                      >
                        {scoringPeriodSelectOptions(leaderboardForm.scoring_period_days).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontFamily: '"Baikal Book"',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#374151',
                        }}
                      >
                        Top ranks to return
                      </label>
                      <select
                        name="top_rank_count"
                        disabled={!leaderboardForm.enabled}
                        value={Number(leaderboardForm.top_rank_count)}
                        onChange={handleLeaderboardFieldChange}
                        style={lbSelectStyle}
                      >
                        {topRankSelectOptions(leaderboardForm.top_rank_count).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div
                    style={{
                      marginBottom: '16px',
                      opacity: leaderboardForm.enabled ? 1 : 0.5,
                      pointerEvents: leaderboardForm.enabled ? 'auto' : 'none',
                    }}
                  >
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                      }}
                    >
                      Which point types count toward the ranking
                    </label>
                    <select
                      name="earning_preset"
                      disabled={!leaderboardForm.enabled}
                      value={leaderboardForm.earning_preset || 'all'}
                      onChange={handleLeaderboardFieldChange}
                      style={{ ...lbSelectStyle, maxWidth: '640px' }}
                    >
                      {LB_SOURCE_PRESETS.filter((p) => p.value !== 'custom').map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                      <option value="custom">Custom (multi-select below)</option>
                    </select>
                  </div>

                  <div
                    style={{
                      marginBottom: '16px',
                      opacity: leaderboardForm.enabled ? 1 : 0.5,
                      pointerEvents: leaderboardForm.enabled ? 'auto' : 'none',
                    }}
                  >
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '13px',
                        color: '#6b7280',
                      }}
                    >
                      Hold Ctrl (Windows) or Cmd (Mac) to select multiple. Most useful when preset is Custom.
                    </label>
                    <select
                      multiple
                      size={7}
                      disabled={!leaderboardForm.enabled}
                      value={leaderboardForm.earning_custom || []}
                      onChange={handleEarningMultiChange}
                      style={{
                        ...lbSelectStyle,
                        maxWidth: '640px',
                        minHeight: '160px',
                        opacity:
                          !leaderboardForm.enabled
                            ? 0.45
                            : leaderboardForm.earning_preset === 'custom'
                              ? 1
                              : 0.45,
                      }}
                    >
                      {LB_EARNING_MULTI.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div
                    style={{
                      marginBottom: '20px',
                      opacity: leaderboardForm.enabled ? 1 : 0.5,
                      pointerEvents: leaderboardForm.enabled ? 'auto' : 'none',
                    }}
                  >
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontFamily: '"Baikal Book"',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#374151',
                      }}
                    >
                      Include ledger rows with blank earning_source when filtering
                    </label>
                    <select
                      name="include_null_earning_source"
                      disabled={!leaderboardForm.enabled}
                      value={leaderboardForm.include_null_earning_source ? 'true' : 'false'}
                      onChange={handleLeaderboardFieldChange}
                      style={lbSelectStyle}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={leaderboardSaving}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: leaderboardSaving ? '#9ca3af' : '#00C8FF',
                      color: leaderboardSaving ? 'white' : '#000000',
                      border: 'none',
                      borderRadius: '8px',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: leaderboardSaving ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {leaderboardSaving ? 'Saving…' : 'Save leaderboard settings'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperuserSchoolManagement;